/**
 * HealthDetailPanel Component
 *
 * Displays detailed information about a selected health advisory (EPA air quality warning)
 * in a slide-out panel on the sidebar. Similar to FireDetailPanel but tailored for
 * health/air quality advisories.
 */

import DOMPurify from 'dompurify';
import { UnifiedEvent, formatRelativeTime } from '../types/events';
import { WindIcon, AlertTriangleIcon, MapPinIcon } from './icons';

interface HealthDetailPanelProps {
  event: UnifiedEvent;
  onClose: () => void;
}

export default function HealthDetailPanel({ event, onClose }: HealthDetailPanelProps) {
  const data = event.data || {};
  const alertLevel = data.alertLevel as string;

  // Purple color scheme for health advisories
  const headerBgColor = 'var(--health-primary)';

  return (
    <div
      className="fire-detail-panel"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--bg-secondary)',
        color: 'var(--text-primary)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: headerBgColor,
          color: 'white',
          padding: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}
      >
        <button
          onClick={onClose}
          style={{
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            fontSize: '20px',
            cursor: 'pointer',
            padding: '4px 10px',
            // 44px touch target — this is the only control that dismisses the
            // panel, and on mobile it sits in the bottom sheet over the map.
            minWidth: 'var(--touch-min, 44px)',
            minHeight: 'var(--touch-min, 44px)',
            flexShrink: 0,
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s',
          }}
          title="Back to list"
          aria-label="Back to list"
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.3)'}
        >
          &larr;
        </button>

        {/* Health icon */}
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: '#fff',
          }}
        >
          <WindIcon size={24} />
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: '12px',
              opacity: 0.9,
              marginBottom: '4px',
              textTransform: 'uppercase',
              fontWeight: '600',
              letterSpacing: '0.5px',
            }}
          >
            {alertLevel || 'Health Advisory'}
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '700',
              lineHeight: 1.3,
            }}
          >
            {event.title}
          </h2>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px',
        }}
      >
        {/* Agency Badge */}
        {data.responsibleAgency ? (
          <div style={{ marginBottom: '16px' }}>
            <span
              style={{
                backgroundColor: 'var(--health-primary-dark)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '600',
              }}
            >
              {String(data.agencyShort || data.responsibleAgency)}
            </span>
          </div>
        ) : null}

        {/* Main Warning Content - HTML from EPA */}
        {(data.webBody || data.warningText) ? (
          <div
            style={{
              backgroundColor: 'rgba(139, 92, 246, 0.15)',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid rgba(139, 92, 246, 0.5)',
            }}
          >
            <h3
              style={{
                margin: '0 0 12px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--health-light)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <AlertTriangleIcon size={16} /> Health Advisory Details
            </h3>
            {/* Render HTML content safely or plain text */}
            {data.webBody ? (
              <div
                style={{
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: 'var(--text-primary)',
                }}
                className="health-warning-content"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(data.webBody)) }}
              />
            ) : (
              <div
                style={{
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: 'var(--text-primary)',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {String(data.warningText)}
              </div>
            )}
            {/* Action instruction if available */}
            {data.action ? (
              <div
                style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  backgroundColor: 'rgba(139, 92, 246, 0.3)',
                  borderRadius: '6px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ fontSize: '16px' }}>👉</span>
                {String(data.action)}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Air Quality Quick Tips */}
        <div
          style={{
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
          }}
        >
          <h3
            style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--health-light)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>💡</span> What You Should Do
          </h3>
          <ul
            style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '13px',
              lineHeight: '1.8',
              color: 'var(--text-primary)',
            }}
          >
            <li>Stay indoors with windows and doors closed</li>
            <li>If you must go outside, wear a P2 or N95 mask</li>
            <li>Avoid outdoor exercise and strenuous activity</li>
            <li>Use air purifiers with HEPA filters if available</li>
            <li>Keep hydrated and monitor for symptoms</li>
          </ul>
        </div>

        {/* Sensitive Groups */}
        <div
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid rgba(239, 68, 68, 0.3)',
          }}
        >
          <h3
            style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--health-danger)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>🫁</span> Sensitive Groups
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: '13px',
              lineHeight: '1.6',
              color: 'var(--text-primary)',
            }}
          >
            People with heart or lung conditions, asthma, those over 65, infants,
            children, and pregnant women are more sensitive to smoke. If you
            experience symptoms, seek medical advice.
          </p>
        </div>

        {/* Location Section */}
        <div
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid var(--border-color)',
          }}
        >
          <h3
            style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <MapPinIcon size={16} /> Affected Area
          </h3>
          {data.location ? (
            <div style={{ marginBottom: '8px', fontSize: '14px', color: 'var(--text-primary)' }}>
              {String(data.location)}
            </div>
          ) : null}
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
            Coordinates: {event.location.lat.toFixed(4)}, {event.location.lon.toFixed(4)}
          </div>
        </div>

        {/* Source Agency */}
        <div
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid var(--border-color)',
          }}
        >
          <h3
            style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>🏛️</span> Source
          </h3>
          {data.responsibleAgency ? (
            <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
              {String(data.responsibleAgency)}
            </div>
          ) : (
            <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
              Environment Protection Authority Victoria
            </div>
          )}
        </div>

        {/* Last Updated */}
        <div
          style={{
            backgroundColor: 'rgba(234, 179, 8, 0.1)',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid rgba(234, 179, 8, 0.3)',
          }}
        >
          <h3
            style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: '600',
              color: '#fbbf24',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>📅</span> Last Updated
          </h3>
          <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
            <div style={{ marginBottom: '8px' }}>
              {new Date(data.lastUpdated ? String(data.lastUpdated) : event.observedAt).toLocaleString()}
              <span style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>
                ({formatRelativeTime(data.lastUpdated ? String(data.lastUpdated) : event.observedAt)})
              </span>
            </div>
          </div>
        </div>

        {/* Distance */}
        {event._distanceKm !== undefined ? (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: '8px',
              marginBottom: '16px',
              textAlign: 'center',
              border: '1px solid var(--border-color)',
            }}
          >
            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text-primary)' }}>{event._distanceKm.toFixed(1)} km</strong> from your location
            </span>
          </div>
        ) : null}
      </div>

      {/* Footer Actions */}
      <div
        style={{
          padding: '16px',
          borderTop: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {/* EPA Air Quality Website */}
        <a
          href="https://www.epa.vic.gov.au/for-community/environmental-information/air-quality/smoke/smoke-your-health"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            width: '100%',
            padding: '12px 16px',
            backgroundColor: 'var(--health-primary)',
            color: 'white',
            textAlign: 'center',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '14px',
          }}
        >
          EPA Victoria - Smoke & Your Health
        </a>

        {/* Check Air Quality */}
        <a
          href="https://www.epa.vic.gov.au/check-air-and-water-quality"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            width: '100%',
            padding: '12px 16px',
            backgroundColor: 'var(--health-primary-dark)',
            color: 'white',
            textAlign: 'center',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '14px',
          }}
        >
          Check Air Quality in Your Area
        </a>

        {/* Google Maps */}
        <a
          href={`https://www.google.com/maps?q=${event.location.lat},${event.location.lon}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            width: '100%',
            padding: '12px 16px',
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-primary)',
            textAlign: 'center',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '14px',
            border: '1px solid var(--border-color)',
          }}
        >
          Open in Google Maps
        </a>
      </div>
    </div>
  );
}
